import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import type { CrawlRunRef, RunComparisonState } from './url-run-comparison.service';

/** Mirrors `backend/src/utils/crawl-run-movers.ts`. */
export type CrawlRunMoverKind = 'winning' | 'decaying';

export interface CrawlRunMoverMetricHint {
  key: string;
  label: string;
  display: string;
  change: number | null;
  improved: boolean | null;
}

export interface CrawlRunMover {
  urlId: string;
  url: string;
  kind: CrawlRunMoverKind;
  metricKey: string;
  metricLabel: string;
  previous: number | null;
  current: number | null;
  change: number;
  display: string;
  serpPosition: CrawlRunMoverMetricHint | null;
  rankedKeywordCount: CrawlRunMoverMetricHint | null;
  issues: CrawlRunMoverIssues;
}

export interface CrawlRunMoverIssueLine {
  severity: 'critical' | 'warning';
  label: string;
  status: 'appeared' | 'resolved' | 'persisting' | 'open';
}

export interface CrawlRunMoverIssues {
  state: string;
  display: string;
  lines: CrawlRunMoverIssueLine[];
  appearedCount: number;
  resolvedCount: number;
  persistingCount: number;
  openCount: number;
  truncated: boolean;
}

export interface CrawlRunSiteMetricCard {
  key: string;
  label: string;
  previous: number | null;
  current: number | null;
  change: number | null;
  /** `${valueDisplay} (${deltaDisplay})` — the canonical one-string form. */
  display: string;
  /** The value alone. The strip renders it at --type-title2. */
  valueDisplay: string;
  /** The movement alone: "+335.9", "no change", "no previous total". */
  deltaDisplay: string;
  compared: boolean;
}

export interface CrawlRunSiteSummary {
  estimatedOrganicTraffic: CrawlRunSiteMetricCard;
  rankedKeywordCount: CrawlRunSiteMetricCard;
  keywordsInTopN: CrawlRunSiteMetricCard;
  keywordsInTopNThreshold: number;
  topKeywordsCap: number;
}

export interface CrawlRunMoversPayload {
  scopeDomain: string;
  runsCaptured: number;
  runs: CrawlRunRef[];
  state: RunComparisonState;
  reason: string | null;
  headline: string;
  currentRun: CrawlRunRef | null;
  previousRun: CrawlRunRef | null;
  comparedMovers: number;
  winningTotal: number;
  decayingTotal: number;
  winning: CrawlRunMover[];
  decaying: CrawlRunMover[];
  truncated: boolean;
  notes: string[];
  siteSummary: CrawlRunSiteSummary | null;
}

@Injectable({ providedIn: 'root' })
export class CrawlRunMoversService {
  private readonly API_URL = environment.apiUrl + '/v1';

  constructor(private http: HttpClient) {}

  getDashboardMovers(limit = 5): Observable<{ success: boolean; data: CrawlRunMoversPayload | null }> {
    return this.http.get<{ success: boolean; data: CrawlRunMoversPayload | null }>(
      `${this.API_URL}/urls/dashboard-movers`,
      { params: { limit: String(limit) } }
    );
  }

  getDomainMovers(
    domain: string,
    options: { crawlRunId?: string; limit?: number } = {}
  ): Observable<{ success: boolean; data: CrawlRunMoversPayload }> {
    const params: Record<string, string> = {};
    if (options.crawlRunId) params['crawlRunId'] = options.crawlRunId;
    if (options.limit != null) params['limit'] = String(options.limit);
    return this.http.get<{ success: boolean; data: CrawlRunMoversPayload }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/crawl-run-movers`,
      { params }
    );
  }
}
